import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import contactService from '../../services/contact.service';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import { LedgerField, LedgerInput, LedgerSelect, LedgerSelectOption } from '../ui';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import { Masthead } from '../shell/Masthead';
import { cn } from '../../lib/cn';

interface ContactFormProps {
  mode?: 'create' | 'edit';
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validationSchema = yup.object({
  first_name: yup
    .string()
    .default('')
    .required('First name is required')
    .test('non-blank', 'First name is required', (value) => Boolean(value && value.trim())),
  last_name: yup
    .string()
    .default('')
    .required('Last name is required')
    .test('non-blank', 'Last name is required', (value) => Boolean(value && value.trim())),
  display_name: yup.string().defined().default(''),
  title: yup.string().defined().default(''),
  company_name: yup.string().defined().default(''),
  contact_type: yup.string().defined().default('INDIVIDUAL'),
  primary_email: yup
    .string()
    .defined()
    .default('')
    .matches(EMAIL_REGEX, { message: 'Invalid email format', excludeEmptyString: true }),
  primary_phone: yup.string().defined().default(''),
  timezone: yup.string().defined().default('America/New_York'),
  preferred_contact_method: yup.string().defined().default('EMAIL'),
  notes: yup.string().defined().default(''),
  is_active: yup.boolean().defined().default(true),
  is_verified: yup.boolean().defined().default(false),
  profile_photo: yup.mixed<File>().nullable().defined().default(null),
});

type ContactFormValues = yup.InferType<typeof validationSchema>;

const defaultValues: ContactFormValues = {
  first_name: '',
  last_name: '',
  display_name: '',
  title: '',
  company_name: '',
  contact_type: 'INDIVIDUAL',
  primary_email: '',
  primary_phone: '',
  timezone: 'America/New_York',
  preferred_contact_method: 'EMAIL',
  notes: '',
  is_active: true,
  is_verified: false,
  profile_photo: null,
};

const CONTACT_TYPE_OPTIONS: LedgerSelectOption[] = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'VENDOR', label: 'Vendor' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'EMPLOYEE', label: 'Employee' },
  { value: 'OTHER', label: 'Other' },
];

const PREFERRED_CONTACT_METHOD_OPTIONS: LedgerSelectOption[] = [
  { value: 'EMAIL', label: 'Email' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'SMS', label: 'SMS' },
  { value: 'OTHER', label: 'Other' },
];

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section aria-label={title}>
    <div className="cap-label mt-5 border-b border-ink pb-0.5">{title}</div>
    <div className="py-3">{children}</div>
  </section>
);

const SwitchField: React.FC<{ label: string; value: boolean; onChange: (value: boolean) => void }> = ({
  label,
  value,
  onChange,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={value}
    aria-label={label}
    onClick={() => onChange(!value)}
    className="flex cursor-pointer items-center gap-2"
  >
    <span aria-hidden className={cn('h-3 w-3 border border-ink', value && 'bg-ink')} />
    <span className="cap-label">{value ? label : `Not ${label.toLowerCase()}`}</span>
  </button>
);

const ContactForm: React.FC<ContactFormProps> = ({ mode = 'create' }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const toast = useToast();
  const { permissions } = usePermissions();
  const companies = permissions?.organizations.companies ?? [];
  const [companyId, setCompanyId] = useState('');

  // New contacts are filed under the user's company; default to their first one
  useEffect(() => {
    const list = permissions?.organizations.companies ?? [];
    if (list.length > 0) {
      setCompanyId((prev) => prev || list[0].id);
    }
  }, [permissions]);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ContactFormValues>({ resolver: yupResolver(validationSchema), defaultValues, mode: 'onTouched' });

  // Tags are list-managed (add/remove chips), not a single text field value,
  // so they live outside RHF alongside the file input's preview state.
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (mode !== 'edit' || !id) return;

    // Discard responses from unmounted runs of this effect: StrictMode's
    // double mount fires two fetches, and a late second reset() would wipe
    // anything typed after the first one applied
    let ignore = false;

    const loadContact = async () => {
      try {
        setLoading(true);
        const contact = await contactService.getContact(id);
        if (!ignore) {
          reset({
            first_name: contact.first_name,
            last_name: contact.last_name,
            display_name: contact.display_name || '',
            title: contact.title || '',
            company_name: contact.company_name || '',
            contact_type: contact.contact_type,
            primary_email: contact.primary_email || '',
            primary_phone: contact.primary_phone || '',
            timezone: contact.timezone || 'America/New_York',
            preferred_contact_method: contact.preferred_contact_method || 'EMAIL',
            notes: contact.notes || '',
            is_active: contact.is_active,
            is_verified: contact.is_verified || false,
            profile_photo: null,
          });
          setTags(contact.tags || []);

          if (contact.profile_photo) {
            setPreviewUrl(contact.profile_photo);
          }
        }
      } catch (error) {
        console.error('Error loading contact:', error);
        if (!ignore) {
          toast.error('Failed to load contact');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadContact();

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id, reset]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setValue('profile_photo', file, { shouldDirty: true });

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const onSubmit = async (values: ContactFormValues) => {
    if (mode === 'create' && !companyId) {
      toast.error('Your account has no company to file this contact under');
      return;
    }

    try {
      setSaving(true);

      const data = new FormData();

      // Updates never move a contact between companies, so only creates send one
      if (mode === 'create') {
        data.append('company', companyId);
      }

      const submission: Record<string, unknown> = { ...values, tags };
      Object.entries(submission).forEach(([key, value]) => {
        if (key === 'tags') {
          data.append(key, JSON.stringify(value));
        } else if (key === 'profile_photo' && value) {
          data.append(key, value as File);
        } else if (value !== null && value !== '') {
          data.append(key, String(value));
        }
      });

      if (mode === 'create') {
        await contactService.createContact(data);
      } else {
        await contactService.updateContact(id!, data);
      }

      // Drop the cached list (and, for edits, the detail) so the redirect
      // shows the saved contact instead of stale data held for staleTime
      queryClient.invalidateQueries({ queryKey: ['contacts-page'] });
      if (mode === 'edit') {
        queryClient.invalidateQueries({ queryKey: ['contact-detail', id] });
      }

      // The list page renders this snackbar after the redirect
      navigate('/contacts', {
        state: {
          snackbar: {
            message: mode === 'create' ? 'Contact created successfully' : 'Contact updated successfully',
            severity: 'success',
          },
        },
      });
    } catch (error: any) {
      console.error('Error saving contact:', error);
      console.error('Error details:', error.response?.data);
      const message = error.response?.data?.detail || JSON.stringify(error.response?.data) || 'Failed to save contact';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const firstName = watch('first_name');
  const lastName = watch('last_name');
  const initials = `${firstName?.[0]?.toUpperCase() ?? ''}${lastName?.[0]?.toUpperCase() ?? ''}`;

  if (loading) {
    return (
      <div>
        <Masthead title={mode === 'create' ? 'Add contact' : 'Edit contact'} />
        <LedgerSkeleton lines={6} />
      </div>
    );
  }

  return (
    <div>
      <Masthead
        title={mode === 'create' ? 'Add contact' : 'Edit contact'}
        summary={mode === 'create' ? 'Create a new contact in your addressbook' : 'Update contact information'}
      />
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Section title="Identity">
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
            <LedgerField label="First name" htmlFor="first_name" error={errors.first_name?.message}>
              <LedgerInput id="first_name" invalid={!!errors.first_name} {...register('first_name')} />
            </LedgerField>
            <LedgerField label="Last name" htmlFor="last_name" error={errors.last_name?.message}>
              <LedgerInput id="last_name" invalid={!!errors.last_name} {...register('last_name')} />
            </LedgerField>
            <LedgerField
              label="Display name"
              htmlFor="display_name"
              hint="Leave blank to use first + last name"
              className="sm:col-span-2"
            >
              <LedgerInput id="display_name" {...register('display_name')} />
            </LedgerField>
            <LedgerField label="Title" htmlFor="title">
              <LedgerInput id="title" {...register('title')} />
            </LedgerField>
            <LedgerField label="Company" htmlFor="company_name">
              <LedgerInput id="company_name" {...register('company_name')} />
            </LedgerField>
          </div>
        </Section>

        <Section title="Type & status">
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
            {mode === 'create' && companies.length > 1 && (
              <LedgerField label="Owning Company">
                <LedgerSelect
                  ariaLabel="Owning Company"
                  value={companyId}
                  options={companies.map((company) => ({ value: company.id, label: company.name }))}
                  onValueChange={setCompanyId}
                />
              </LedgerField>
            )}
            <LedgerField label="Contact type">
              <Controller
                name="contact_type"
                control={control}
                render={({ field }) => (
                  <LedgerSelect
                    ariaLabel="Contact type"
                    value={field.value}
                    options={CONTACT_TYPE_OPTIONS}
                    onValueChange={field.onChange}
                  />
                )}
              />
            </LedgerField>
            <div className="flex items-center gap-5">
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => <SwitchField label="Active" value={field.value} onChange={field.onChange} />}
              />
              <Controller
                name="is_verified"
                control={control}
                render={({ field }) => <SwitchField label="Verified" value={field.value} onChange={field.onChange} />}
              />
            </div>
          </div>
        </Section>

        <Section title="Contact info">
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
            <LedgerField label="Primary email" htmlFor="primary_email" error={errors.primary_email?.message}>
              <LedgerInput id="primary_email" type="email" invalid={!!errors.primary_email} {...register('primary_email')} />
            </LedgerField>
            <LedgerField label="Primary phone" htmlFor="primary_phone">
              <LedgerInput id="primary_phone" {...register('primary_phone')} />
            </LedgerField>
            <LedgerField label="Timezone" htmlFor="timezone">
              <LedgerInput id="timezone" {...register('timezone')} />
            </LedgerField>
            <LedgerField label="Preferred contact method">
              <Controller
                name="preferred_contact_method"
                control={control}
                render={({ field }) => (
                  <LedgerSelect
                    ariaLabel="Preferred contact method"
                    value={field.value}
                    options={PREFERRED_CONTACT_METHOD_OPTIONS}
                    onValueChange={field.onChange}
                  />
                )}
              />
            </LedgerField>
          </div>
        </Section>

        <Section title="Details">
          <LedgerField label="Notes" htmlFor="notes">
            <textarea
              id="notes"
              rows={4}
              className="w-full border border-rule-faint bg-transparent p-2 text-scale-1 text-ink outline-none focus:border-accent"
              {...register('notes')}
            />
          </LedgerField>

          <div className="mt-3">
            <label htmlFor="new-tag" className="cap-label block">
              Tags
            </label>
            {tags.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span key={tag} className="cap-label inline-flex items-center gap-1.5 border border-ink px-2 py-0.5">
                    {tag}
                    <button
                      type="button"
                      aria-label={`Remove ${tag}`}
                      onClick={() => handleRemoveTag(tag)}
                      className="cursor-pointer hover:!text-ink"
                    >
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="mt-2 flex gap-2">
              <LedgerInput
                id="new-tag"
                placeholder="Add a tag"
                value={newTag}
                onChange={(event) => setNewTag(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="shrink-0 cursor-pointer border border-ink px-3 py-1 text-scale-1 text-ink hover:bg-paper-raised"
              >
                Add
              </button>
            </div>
          </div>
        </Section>

        <Section title="Photo">
          <div className="flex flex-col items-center gap-3">
            {previewUrl ? (
              <img src={previewUrl} alt="Profile preview" className="h-28 w-28 rounded-full border border-ink object-cover" />
            ) : (
              <div
                aria-hidden
                className="flex h-28 w-28 items-center justify-center rounded-full border border-ink font-serif text-scale-4 text-ink-muted"
              >
                {initials}
              </div>
            )}
            <label className="cursor-pointer border border-ink px-3 py-1 text-scale-1 text-ink hover:bg-paper-raised">
              Upload photo
              <input type="file" accept="image/*" className="sr-only" onChange={handleFileChange} />
            </label>
          </div>
        </Section>

        <div className="mt-5 flex gap-3 border-t border-ink pt-4">
          <button
            type="submit"
            disabled={saving}
            className="cursor-pointer bg-accent px-3 py-1.5 text-scale-1 font-semibold text-paper hover:bg-accent-hover disabled:opacity-60"
          >
            {saving ? 'Saving…' : mode === 'create' ? 'Create Contact' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/contacts')}
            className="cursor-pointer border border-ink px-3 py-1.5 text-scale-1 text-ink hover:bg-paper-raised"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
